(ns io.daskul.tutorial.webapp.app.core
  (:require
   [clojure.string :as str]
   [uix.core :refer [defui $ use-effect use-state use-callback]]
   [uix.dom]
   [uix.re-frame :as urf]
   [re-frame.core :as rf]
   [day8.re-frame.http-fx] ;; Register :http-xhrio effect
   [io.daskul.tutorial.webapp.effects] ;; Register :notification effect
   [io.daskul.tutorial.webapp.app.events :as app.events]
   [io.daskul.tutorial.webapp.router :as router]
   [io.daskul.webapp.routes :as shared-routes]
   [io.daskul.webapp.config.subs :as config.subs]
   [io.daskul.webapp.views.aggregate :as aggregate]
   [io.daskul.tutorial.webapp.views.login :as login]
   ;; Product-specific components for aggregate view injection
   [io.daskul.tutorial.webapp.views.invoice-create :as invoice-create]
   [io.daskul.tutorial.webapp.views.invoice-edit :as invoice-edit]
   [io.daskul.tutorial.webapp.views.session-attendee-edit :as sa-edit]
   [io.daskul.tutorial.webapp.components.guardian-panel :as guardian-panel]
   [io.daskul.tutorial.webapp.components.invoice-lines :as invoice-lines]
   [io.daskul.tutorial.webapp.components.session-attendee-add :as sa-add]
   [io.daskul.tutorial.webapp.components.footer :as footer]
   [io.daskul.tutorial.webapp.views.change-password :as change-password]
   [io.daskul.tutorial.webapp.views.today :as today]
   [io.daskul.tutorial.webapp.views.analytics :as analytics]
   [io.daskul.tutorial.webapp.views.calendar :as calendar]
   ;; SMS-178: registers the Trial Bookings :main/before slot for :lead
   ;; (F-057) — required here, next to aggregate/configure!.
   [io.daskul.tutorial.webapp.leads]
   [io.daskul.tutorial.webapp.views.inbox :as inbox]
   [io.daskul.tutorial.webapp.views.conversation-thread :as conversation-thread]
   [io.daskul.tutorial.webapp.views.settings :as settings]
   [io.daskul.tutorial.webapp.views.boot-splash :as boot-splash]
   [io.daskul.webapp.widgets.dashboard.events :as dashboard.events]
   [io.daskul.tutorial.webapp.domain.auth.events :as auth.events]
   [io.daskul.tutorial.webapp.domain.auth.subs :as auth.subs]
   [io.daskul.tutorial.webapp.domain.bootstrap.events]
   [io.daskul.tutorial.webapp.domain.bootstrap.subs :as bootstrap.subs]
   [io.daskul.tutorial.webapp.domain.active-context.subs :as ac.subs]
   [io.daskul.tutorial.webapp.hooks.feature-flags :as ff]
   [io.daskul.tutorial.webapp.domain.feature-flags.subs :as ff.subs]
   [io.daskul.tutorial.webapp.components.branch-picker :as branch-picker]
   [io.daskul.tutorial.webapp.shared.sentry :as sentry]
   [io.daskul.tutorial.webapp.config :as config]
   ;; Library
   [io.daskul.webapp.core :as webapp]
   [io.daskul.webapp.render-flush :as render-flush]
   [io.daskul.webapp.nav :as nav]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.aggregates.subs :as agg.subs]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.notifications :as notif]
   ;; Shared
   [io.daskul.tutorial.webapp.shared.api.client :as client]
   [io.daskul.tutorial.webapp.shared.log :as log]))

;; ============================================================================
;; Navigation Sidebar
;; ============================================================================

(def ^:private nav-icons
  "Map aggregate keys to their icons."
  {:org-unit icons/icon-building
   :person icons/icon-user-circle
   :user icons/icon-users
   :student icons/icon-user
   :enrollment icons/icon-clipboard-list
   :teacher icons/icon-user
   :program icons/icon-folder
   :package icons/icon-package
   :grade-level icons/icon-list
   :guardian icons/icon-users
   :student-contact icons/icon-address-book
   :lead icons/icon-user-circle
   :conversation icons/icon-message-circle
   :discount icons/icon-discount
   :invoice icons/icon-receipt
   :payment icons/icon-cash
   :class icons/icon-school
   :session icons/icon-clock
   :expense icons/icon-trending-down
   :expense-category icons/icon-tag})

(def ^:private nav-config
  "Navigation structure per role.
   Map entries = grouped (with section header), vector entries = flat (no header).
   Aggregates not listed here don't appear in the sidebar."
  {"school-admin"
   [;; Flat (no header): Analytics sits directly under Today — the strategic
    ;; view next to the operational one.
    [{:route :analytics :label "Analytics" :icon icons/icon-chart-bar}]
    {:group "Messaging" :feature-flags ["facebook-messaging"] :items [{:route :inbox :label "Conversations"
                                 :icon icons/icon-message-circle}]}
    ;; #860: no top-level Guardians browse tile — guardians are records reached
    ;; via a student's Guardian tab; unlinked ones via the Today orphan card
    ;; (#859). The generic /a/guardians route stays for those deep links + detail.
    {:group "People"    :items [:student :lead]}
    {:group "Academics" :items [:program :enrollment :class :session
                                {:route :calendar :label "Calendar" :icon icons/icon-calendar}]}
    {:group "Billing"   :items [:invoice :payment :discount]}
    ;; #729: P&L folded into Analytics, so this group is now expenses-only —
    ;; renamed from "Finance" to match what's under it (Billing is money in,
    ;; Expenses is money out).
    {:group "Expenses"  :items [:expense :expense-category]}]

   "branch-admin"
   ;; #729: Analytics is hidden for branch-admin — the API gives them no
   ;; analytics sections (neither the school's overhead nor its margins), so the
   ;; page would render as a bare Overview snapshot. They keep Today + records.
   [{:group "Messaging" :feature-flags ["facebook-messaging"] :items [{:route :inbox :label "Conversations"
                                 :icon icons/icon-message-circle}]}
    ;; #860: no top-level Guardians browse tile (see school-admin note above).
    {:group "People"    :items [:student :lead]}
    {:group "Academics" :items [:enrollment :class :session
                                {:route :calendar :label "Calendar" :icon icons/icon-calendar}]}
    {:group "Billing"   :items [:invoice :payment :discount]}
    {:group "Expenses"  :items [:expense]}]

   "teacher"
   [[:student :enrollment]]

   "guardian"
   [[:student :enrollment]]

   "student"
   [[:enrollment]]})

(defn- resolve-nav-item
  "Resolve a single nav item. Keywords are aggregate keys (looked up in config).
   Maps with :route are custom entries, shown unless gated off by :feature-flags."
  [aggregates flags item]
  (cond
    (keyword? item)
    (when-let [c (get aggregates item)]
      {:type :item :agg-key item :config c})

    (and (map? item) (:route item) (nav/visible? item flags))
    {:type :custom-item :route (:route item) :label (:label item) :icon (:icon item)}))

(defn- resolve-nav-entries
  "Resolve nav config into a flat renderable list.
   Returns [{:type :header :label \"People\"} {:type :item :agg-key :student :config {...}} ...]
   Skips aggregates not present in the loaded config, and elements gated off by
   :feature-flags. Supports custom route entries as maps with :route key."
  [entries aggregates flags]
  (into []
    (mapcat
      (fn [entry]
        (if (and (map? entry) (:group entry))
          ;; Grouped: skip when the group itself is gated off, or no items resolve.
          (when (nav/visible? entry flags)
            (let [items (keep #(resolve-nav-item aggregates flags %) (:items entry))]
              (when (seq items)
                (cons {:type :header :label (:group entry)} items))))
          ;; Flat: emit items only
          (keep #(resolve-nav-item aggregates flags %) entry))))
    entries))

(defui nav-sidebar [{:keys [on-navigate]}]
  (let [aggregates (urf/use-subscribe [::config.subs/aggregates])
        role (urf/use-subscribe [::auth.subs/role])
        flags (urf/use-subscribe [::ff.subs/all])
        route (urf/use-subscribe [::router/current-route])
        route-name (get-in route [:data :name])
        current-agg (get-in route [:path-params :agg])
        entries (resolve-nav-entries (get nav-config role) aggregates flags)
        ;; SMS-29: app-shell-level poll (see main-panel) keeps this current
        ;; from any page, not just while Inbox is mounted.
        unread-counts (urf/use-subscribe [::agg.subs/counts :conversation :unread])
        unread-n (or (get unread-counts :true) (get unread-counts "true") 0)]
    ($ ui/stack {:gap 2}
      ;; SMS-144: Today is the task-based home (0013 §4 — replaces the generic
      ;; Dashboard as the post-login landing). The home route now renders the
      ;; Today command center for admins (see main-content).
      (when (#{"school-admin" "branch-admin"} role)
        ($ ui/nav-link
          {:key "today"
           :label "Today"
           :leftSection ($ icons/icon-dashboard {:size 18 :stroke 1.5})
           :active (= :home route-name)
           :onClick on-navigate
           :href (router/href :home)}))
      (for [{entry-type :type :keys [agg-key config label route icon] :as entry} entries]
        (case entry-type
          :header
          ($ ui/text {:key label
                      :size "xs" :fw 500 :c "dimmed" :tt "uppercase"
                      :px "sm" :pt "md" :pb 4}
            label)

          :item
          (let [nav-icon (get nav-icons agg-key icons/icon-building)
                resource (subs (:base-path config) 1)
                active? (= resource current-agg)]
            ($ ui/nav-link
              {:key (name agg-key)
               :label (:label config)
               :leftSection ($ nav-icon {:size 18 :stroke 1.5})
               :active active?
               :onClick on-navigate
               :href (router/href :aggregate/list {:agg resource})}))

          :custom-item
          (let [nav-icon (or icon icons/icon-building)]
            ($ ui/nav-link
              {:key (name route)
               :label label
               :leftSection ($ nav-icon {:size 18 :stroke 1.5})
               :rightSection (when (and (= :inbox route) (pos? unread-n))
                               ($ ui/badge {:size "sm" :variant "light" :circle true} unread-n))
               :active (= route route-name)
               :onClick on-navigate
               :href (router/href route)})))))))

;; ============================================================================
;; Main Content Router
;; ============================================================================

(defui page-not-found []
  ($ ui/stack {:gap "md"}
    ($ ui/title {:order 2} "Page not found")
    ($ ui/text {:c "dimmed"}
      "The page you're looking for doesn't exist.")))

(defui main-content []
  (let [route (urf/use-subscribe [::router/current-route])
        route-name (get-in route [:data :name])
        role (urf/use-subscribe [::auth.subs/role])
        messaging? (ff/use-feature-flag "facebook-messaging")
        ;; #593: the conversation thread renders its own in-header back control
        ;; (a 44px target that routes to the Inbox explicitly, rather than
        ;; window.history). A second, generic Back link above it would be a
        ;; redundant affordance — and on mobile it is chrome stacked on top of a
        ;; view that has to fill the viewport exactly.
        ;; Aggregate detail is the same case: the record header renders Back
        ;; inside its own band, so the generic link above would be a second
        ;; affordance for one action — and it landed outside the band, on the
        ;; page surface, with the band's edge cutting through it.
        show-back? (and route-name
                        (not= route-name :home)
                        (not= route-name :conversation/thread)
                        (not (contains? shared-routes/detail-route-names route-name)))
        content (case route-name
                  ;; SMS-144: Today (command center) is the admin home, replacing
                  ;; the generic Dashboard (0013 §4 "Home is task-based").
                  :home
                  (if (#{"school-admin" "branch-admin"} role)
                    ($ today/today-page)
                    ($ ui/stack {:gap "md"}
                      ($ ui/title {:order 2} "Welcome")
                      ($ ui/text "Select an item from the sidebar to get started.")))

                  (:aggregate/list :aggregate/new :aggregate/detail :aggregate/detail-tab)
                  ($ aggregate/aggregate-page)

                  :inbox
                  (if messaging?
                    ($ inbox/inbox-page)
                    ($ page-not-found))

                  :conversation/thread
                  (if messaging?
                    ($ conversation-thread/conversation-thread-page)
                    ($ page-not-found))

                  :calendar
                  ($ calendar/calendar-page)

                  ;; #722: the dashboard sections (Overview + the date-filtered
                  ;; Revenue / Enrollment Health analytics) had no route at all
                  ;; after SMS-144 made Today the admin home — the endpoint was
                  ;; live with nothing rendering it. Admin-only, as the server
                  ;; enforces; the page itself drops :live (Today owns it).
                  :analytics
                  (if (#{"school-admin" "branch-admin"} role)
                    ($ analytics/analytics-page)
                    ($ page-not-found))

                  :change-password
                  ($ change-password/change-password-page)

                  :settings
                  ($ settings/settings-page)

                  ;; Default / loading
                  ($ ui/loader))]
    ($ :<>
      (when show-back?
        ($ ui/anchor
          {:onClick #(router/back! route)
           :size "sm"
           :c "dimmed"
           :mb "xs"
           :style {:display "inline-flex" :alignItems "center" :gap "4px" :cursor "pointer"}}
          ($ icons/icon-arrow-left {:size 14})
          "Back"))
      content)))

;; ============================================================================
;; App Shell
;; ============================================================================

(defui impersonation-banner []
  (let [user (urf/use-subscribe [::auth.subs/current-user])
        display-name (str (:first-name user) " " (:last-name user))
        reason (get-in user [:impersonator :reason])]
    ($ ui/box {:bg "orange" :py 6 :px "md"}
      ($ ui/stack {:align "center" :gap 0}
        ($ ui/group {:justify "center" :gap 8}
          ($ icons/icon-alert-triangle {:size 16 :color "white"})
          ($ ui/text {:size "sm" :fw 500 :c "white"}
            (str "Impersonating " display-name " (" (:email user) ", " (:role user) ")")))
        (when reason
          ($ ui/text {:size "xs" :c "white" :opacity 0.85}
            (str "Reason: " reason)))))))

(defui environment-banner []
  ($ ui/tooltip {:label "This is a non-production environment. Do not use for actual work — for testing and demo purposes only."
                 :multiline true :w 300 :withArrow true}
    ($ ui/badge {:color "yellow.6" :variant "filled" :size "lg" :radius "sm"
                 :style {:cursor "help"}
                 :rightSection ($ icons/icon-info-circle {:size 14})}
      "Non-production environment")))

(defui sidebar-user-menu [{:keys [close-nav]}]
  (let [user (urf/use-subscribe [::auth.subs/current-user])
        display-name (str (:first-name user) " " (:last-name user))
        role-label (some-> (:role user)
                           (str/replace "-" " ")
                           str/capitalize)]
    ($ ui/box {:p "md"
               :style {:borderTop "1px solid var(--mantine-color-gray-3)"}}
      ($ ui/menu {:position "top-start" :shadow "md"}
        ($ ui/menu-target
          ($ ui/unstyled-button {:w "100%"}
            ($ ui/group {:gap "xs"}
              ($ icons/icon-user-circle {:size 20 :stroke 1.5})
              ($ ui/stack {:gap 0 :style {:flex 1}}
                ($ ui/text {:size "sm" :fw 500} display-name)
                ($ ui/text {:size "xs" :c "dimmed"} role-label))
              ($ icons/icon-chevron-up {:size 16 :stroke 1.5
                                        :color "var(--mantine-color-dimmed)"}))))
        ($ ui/menu-dropdown
          ($ ui/menu-item
            {:leftSection ($ icons/icon-user-circle {:size 16 :stroke 1.5})
             :onClick (fn []
                        (close-nav)
                        (router/navigate! :settings))}
            "Settings")
          ($ ui/menu-item
            {:leftSection ($ icons/icon-logout {:size 16 :stroke 1.5})
             :color "red"
             :onClick #(rf/dispatch [::auth.events/logout])}
            "Logout"))))))

(def ^:private unread-poll-interval-ms
  "SMS-29: app-shell-level poll for the Inbox nav badge — mirrors the existing
   conversation-thread poll-interval-ms so unread state stays fresh (within
   one cycle) from any page, not just while Inbox is mounted."
  5000)

(defui main-panel [{:keys [impersonating?]}]
  (let [[nav-opened set-nav-opened] (use-state false)
        toggle-nav (use-callback #(set-nav-opened not) [])
        close-nav (use-callback #(set-nav-opened false) [])
        route (urf/use-subscribe [::router/current-route])
        ;; #593: the conversation thread is a full-bleed chat surface on mobile —
        ;; it owns the viewport below the app header so that the message list is
        ;; the only scroll context and the composer stays pinned on screen. That
        ;; only works if the shell stops padding it and stops appending a footer
        ;; underneath, both of which would add page-level scroll. Suppressed
        ;; below `sm` only; desktop keeps the normal padded page + footer.
        chat-route? (= (get-in route [:data :name]) :conversation/thread)
        role (urf/use-subscribe [::auth.subs/role])
        messaging? (ff/use-feature-flag "facebook-messaging")
        hostname (.. js/window -location -hostname)
        non-prod? (or (= "localhost" hostname)
                      (= "127.0.0.1" hostname)
                      (str/starts-with? hostname "staging-")
                      (str/starts-with? hostname "dev-")
                      (str/starts-with? hostname "demo-")
                      (str/includes? hostname "-staging")
                      (str/includes? hostname "-dev")
                      (str/includes? hostname "-demo"))]

    ;; SMS-29: poll the unread-conversations count for the Inbox nav badge,
    ;; independent of which page is mounted. Gated on the same role + feature
    ;; flag that gate the Inbox nav entry itself — no point polling otherwise.
    (use-effect
     (fn []
       (when (and messaging? (#{"school-admin" "branch-admin"} role))
         (rf/dispatch [::agg.events/counts :conversation :unread {}])
         (let [timer (js/setInterval
                      #(rf/dispatch [::agg.events/counts :conversation :unread {}])
                      unread-poll-interval-ms)]
           (fn [] (js/clearInterval timer)))))
     [messaging? role])
    ($ ui/app-shell
      {:layout "alt"
       :header #js {:height (+ 60 (if impersonating? 50 0))}
       :navbar #js {:width 260 :breakpoint "sm"
                    :collapsed #js {:mobile (not nav-opened) :desktop false}}
       ;; "0px", never a bare 0: Mantine builds Main's offset as
       ;; `calc(var(--app-shell-header-offset) + var(--app-shell-padding))`, and
       ;; calc() rejects a unitless zero in addition — the whole declaration goes
       ;; invalid and padding-top collapses to 0, dropping the page *under* the
       ;; fixed header. Same reason every responsive 0 below carries a unit.
       :padding (if chat-route?
                  #js {:base "0px" :sm "md"}
                  #js {:base "xs" :sm "md"})}
      ;; Header
      ($ ui/app-shell-header {:withBorder false
                              :bg (if non-prod? "yellow.1" "gray.0")
                              :style (when non-prod?
                                       {:border-bottom "2px solid var(--mantine-color-yellow-4)"})}
        (when impersonating?
          ($ impersonation-banner))
        ($ ui/group {:h 60 :px "md" :gap "md"}
          ($ ui/burger {:opened nav-opened
                        :onClick toggle-nav
                        :hiddenFrom "sm"
                        :size "sm"})
          ($ branch-picker/workspace-switcher)
          (when non-prod?
            ($ ui/tooltip {:label "Non-production environment — for testing and demo only"
                           :multiline true :w 250 :withArrow true}
              ($ ui/action-icon {:variant "transparent" :color "yellow.8" :size "sm"}
                ($ icons/icon-alert-triangle {:size 18}))))))      ;; Sidebar — logo header + nav links + user menu footer (navbar-simple pattern)
        ($ ui/app-shell-navbar
          ($ ui/stack {:h "100%" :gap 0 :p "md"}
            ;; Logo header
            ($ ui/group {:gap 10 :pl "sm" :pb "md" :mb "md"
                         :style {:borderBottom "1px solid var(--mantine-color-gray-3)"}}
              ($ :img {:src "/klei-wordmark.svg" :alt "Klei"
                       :width 65 :height 28}))
            ;; Navigation links (scrollable)
            ($ ui/scroll-area {:flex 1}
              ($ nav-sidebar {:on-navigate close-nav}))
            ;; User menu footer
            ($ sidebar-user-menu {:close-nav close-nav})))
      ;; Main content
      ;; --surface-page, not Mantine's gray.0. gray.0 is #f8f9fa, a COOL grey;
      ;; the design system's page field is #faf8f5, a warm sand. Everything
      ;; else on the page is already drawn from the sand ramp, so a cool ground
      ;; under warm cards is what made the app read as a different palette from
      ;; the reference even where every component matched.
      ($ ui/app-shell-main {:style {:background "var(--surface-page)"}}
        ($ ui/container {:size "100%"
                         :px (when chat-route? #js {:base "0px" :sm "md"})}
          ($ ui/stack {:gap 0
                       :mih (if chat-route?
                              #js {:base "0px" :sm "calc(100vh - 60px - 32px)"}
                              "calc(100vh - 60px - 32px)")
                       :justify "space-between"}
            ($ ui/box {:py (if chat-route? #js {:base "0px" :sm "md"} "md")}
              ($ main-content))
            ;; Chat routes drop the footer on mobile only — it would sit below a
            ;; view that already fills the viewport and reintroduce page scroll.
            ($ ui/box {:visibleFrom (when chat-route? "sm")}
              ($ footer/app-footer))))))))

(defui school-not-found []
  ($ ui/center {:style {:min-height "100vh"}}
    ($ ui/stack {:align "center" :gap "md"}
      ($ ui/title {:order 2} "School not found")
      ($ ui/text {:c "dimmed" :size "lg"}
        "The school code in the URL is not valid.")
      ($ ui/text {:c "dimmed" :size "sm"}
        "Please check the URL and try again."))))

(defui root-page []
  (let [[code set-code] (use-state "")
        [error set-error] (use-state nil)
        on-submit (use-callback
                   (fn [e]
                     (.preventDefault e)
                     (let [trimmed (str/trim code)]
                       (if (seq trimmed)
                         (do (set-error nil)
                             (set! (.. js/window -location -href)
                                   (str "/" trimmed "/login")))
                         (set-error "Please enter your school code"))))
                   [code])]
    ($ ui/box {:class "login-page"}
      ($ login/brand-panel)
      ($ ui/box {:class "login-form-panel"}
        ($ ui/stack {:gap "xl" :w "100%" :maw 380}
          ($ ui/stack {:gap "sm" :align "center"}
            ($ ui/title {:order 3 :ta "center"} "Welcome to Klei")
            ($ ui/text {:c "dimmed" :size "sm" :ta "center"}
              "Each school has its own login page. Please use the login link provided by your school administrator."))
          ($ ui/divider {:label "or" :labelPosition "center" :my "xs"})
          ($ :form {:on-submit on-submit}
            ($ ui/stack {:gap "md"}
              ($ ui/text {:size "sm" :ta "center" :c "dimmed"}
                "If you know your school code, enter it below:")
              ($ ui/text-input
                {:placeholder "your-school-code"
                 :value code
                 :onChange #(set-code (.. % -target -value))
                 :error error
                 :size "md"})
              ($ ui/button {:type "submit" :fullWidth true :size "md"
                            :variant "light"}
                "Go to school login")))
          ($ footer/app-footer))))))

(defui app []
  (let [route (urf/use-subscribe [::router/current-route])
        route-name (get-in route [:data :name])
        public? (get-in route [:data :public?])
        authenticated? (urf/use-subscribe [::auth.subs/authenticated?])
        boot-phase (urf/use-subscribe [::bootstrap.subs/phase])
        impersonating? (urf/use-subscribe [::auth.subs/impersonating?])
        school-not-found? (urf/use-subscribe [::app.events/school-not-found?])]

    ;; Auth guard: redirect to login if not authenticated on protected route
    (use-effect
     (fn []
       (when (and (not public?) (not authenticated?) route-name)
         (router/navigate! :login)))
     [public? authenticated? route-name])

    ;; Redirect to home if authenticated and on login page
    (use-effect
     (fn []
       (when (and (= route-name :login) authenticated?)
         (router/navigate! :home)))
     [route-name authenticated?])

    ;; Bare /<school-code> isn't a real page — send it to the school login
    ;; (or home if already signed in), not the platform chooser (SMS-87).
    (use-effect
     (fn []
       (when (= route-name :school-root)
         (router/navigate! (if authenticated? :home :login))))
     [route-name authenticated?])

    ($ ui/mantine-provider
      (cond
        ;; Root page (no school code in URL)
        (= route-name :root)
        ($ root-page)

        ;; Invalid school code
        school-not-found?
        ($ school-not-found)

        ;; No route matched — show guidance
        (nil? route-name)
        ($ root-page)

        ;; Show login page (doesn't need config)
        (= route-name :login)
        ($ login/login-page)

        ;; Bare /<school-code> — redirecting (see effect above); show a loader,
        ;; not the platform chooser or a half-rendered shell.
        (= route-name :school-root)
        ($ ui/center {:style {:min-height "100vh"}}
          ($ ui/loader))

        ;; Show main app once the boot/readiness gate is :ready
        (and authenticated? (= :ready boot-phase))
        ($ main-panel {:impersonating? impersonating?})

        ;; A hard-required fetch failed — offer Retry, never an infinite spinner
        (and authenticated? (= :error boot-phase))
        ($ boot-splash/boot-error)

        ;; Booting — branded splash (delay-before-show keeps fast boots clean)
        (and authenticated? (= :loading boot-phase))
        ($ boot-splash/boot-loading)

        ;; :idle / redirecting
        :else
        ($ ui/center {:style {:min-height "100vh"}}
          ($ ui/loader))))))

(defonce root
  (uix.dom/create-root (js/document.getElementById "app")))

(defn ^:dev/after-load reload []
  (uix.dom/render-root ($ app) root))

(defn init []
  (sentry/init! (let [{:keys [version git-sha]} (config/build-info)]
                  (assoc (config/sentry-config)
                         :release (str version " (" git-sha ")"))))
  ;; Hidden/occluded tabs suspend rAF, freezing reagent's render flush while
  ;; re-frame events keep mutating app-db — the page looks dead until the tab
  ;; is shown. See docs/ui/hidden-tab-rendering.md (SMS-179).
  (render-flush/install!)
  (rf/dispatch-sync [::app.events/initialize-db])
  (rf/dispatch-sync [::auth.events/initialize])
  ;; Configure lib with static config (resource-names added in config/events fetch-success)
  (webapp/configure! {:api-base client/api-url
                      :auth-header client/auth-header
                      :log-error (fn [msg data] (log/error msg data))
                      :get-label (fn [db agg-key]
                                   (get-in db [:config :data :aggregates agg-key :label-singular]))
                      :on-http-error (fn [status _error]
                                       (when (= status 401)
                                         {:dispatch [:io.daskul.tutorial.webapp.shared.events/unauthorized]}))
                      :notify-success (fn [title msg] {:notification (notif/success title msg)})
                      :notify-error (fn [title msg] {:notification (notif/error title msg)})})
  ;; Configure dashboard with product-specific href resolution
  (dashboard.events/configure!
   {:href-fn (fn [route-name path-params query-params]
               (router/href route-name path-params query-params))})
  ;; Configure aggregate views with product-specific callbacks
  (aggregate/configure!
   {:navigate-fn (fn [route-name params & [query-params]]
                   (router/navigate! route-name params query-params))
    :href-fn (fn [route-name params] (router/href route-name params))
    :current-route-sub [::router/current-route]
    ;; The record header renders its own Back inside the band (F-060 zone 1).
    :back-fn (fn [] (router/back! @(rf/subscribe [::router/current-route])))
    :push-query-params-fn (fn [route-name path-params query-params]
                            (router/push-query-params! route-name path-params query-params))
    :replace-query-params-fn (fn [route-name path-params query-params]
                               (router/replace-query-params! route-name path-params query-params))
    :custom-edit-details {:invoice invoice-edit/details-card
                          :session-attendee sa-edit/details-card}
    :custom-child-add-components {[:guardian :invoice] invoice-create/inline-from-guardian
                                  [:session :session-attendee] sa-add/add-panel}
    ;; #843 slice 6: the invoice line-item tab renders the SAME shared
    ;; line-editor the create page uses (anti-drift), wired to live
    ;; /invoice-line-items CRUD — replacing the old billables-add panel.
    ;; #974: the student's Guardian tab is the payer, their other students and
    ;; this student's discounts — not a table of link rows pointing off the page.
    :custom-child-panels {[:invoice :invoice-line-item] invoice-lines/invoice-lines-panel
                          [:student :student-guardian] guardian-panel/guardian-panel}
    :custom-create-pages {:invoice invoice-create/create-page}
    ;; Progressive capture on the standalone Create Lead form (SMS-106). Empty
    ;; :filters matches every leads list state, supplies no label (so the page
    ;; title is unchanged), and only carries the :create {:advanced ...} set:
    ;; the agreed primary fields (branch, channel, name, phone, program, source)
    ;; stay up-front; social handle, email, student name + age collapse under
    ;; "More details".
    :views {:lead [{:filters {}
                    :create {:advanced #{:social-media-name :email
                                         :student-first-name :student-last-name
                                         :age}}}]
            ;; #859: the guardian list is only reached as the "Unlinked guardians"
            ;; review surface (from the Today card). When that filter is active,
            ;; retitle it and explain the task — otherwise it looks like a plain
            ;; browse listing with no hint of what to do.
            :guardian [{:filters {:link-status "unlinked"}
                        :label "Unlinked guardians"
                        :description (str "These guardians aren't linked to any student. "
                                          "Open one to link it to a student, or remove it.")}]}})
  (router/init!)
  (reload))
